import React, { useState } from 'react';
import { ChromePicker } from 'react-color';
import { Customer } from '../types';
import { supabase } from '../lib/supabase';

interface CustomerModalProps {
  customer: Customer;
  onClose: () => void;
  onSave: (customer: Customer) => void;
  isNew?: boolean;
}

export function CustomerModal({ customer, onClose, onSave, isNew = false }: CustomerModalProps) {
  const [editedCustomer, setEditedCustomer] = useState<Customer>({
    ...customer,
    contacts: customer.contacts || []
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newContact, setNewContact] = useState('');
  const [showColorPicker, setShowColorPicker] = useState(false);

  // Generate years array in descending order from current year to 2018
  const years = Array.from(
    { length: new Date().getFullYear() - 2017 },
    (_, i) => (new Date().getFullYear() - i).toString()
  );

  const handleSave = async () => {
    setLoading(true);
    setError(null);

    try {
      if (isNew) {
        const { data, error: createError } = await supabase
          .from('customers')
          .insert({
            name: editedCustomer.name,
            year_started: editedCustomer.year_started,
            contacts: editedCustomer.contacts || [],
            color: editedCustomer.color
          })
          .select()
          .single();

        if (createError) throw createError;
        if (!data) throw new Error('Failed to create customer');

        onSave(data);
      } else {
        const { data, error: updateError } = await supabase
          .from('customers')
          .update({
            name: editedCustomer.name,
            year_started: editedCustomer.year_started,
            contacts: editedCustomer.contacts || [],
            color: editedCustomer.color
          })
          .eq('id', customer.id)
          .select()
          .single();

        if (updateError) throw updateError;
        if (!data) throw new Error('Failed to update customer');

        onSave(data);
      }
    } catch (error) {
      console.error('Error saving customer:', error);
      setError(error instanceof Error ? error.message : 'An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  const addContact = () => {
    if (newContact.trim()) {
      setEditedCustomer({
        ...editedCustomer,
        contacts: [...(editedCustomer.contacts || []), newContact.trim()]
      });
      setNewContact('');
    }
  };

  const removeContact = (index: number) => {
    setEditedCustomer({
      ...editedCustomer,
      contacts: (editedCustomer.contacts || []).filter((_, i) => i !== index)
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-hidden">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900">
              {isNew ? 'Add Customer' : 'Edit Customer'}
            </h2>
          </div>

          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Customer Name
              </label>
              <input
                type="text"
                value={editedCustomer.name}
                onChange={(e) => setEditedCustomer({ ...editedCustomer, name: e.target.value })}
                className="w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="Enter customer name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Year Started
              </label>
              <select
                value={editedCustomer.year_started || ''}
                onChange={(e) => setEditedCustomer({ ...editedCustomer, year_started: e.target.value })}
                className="w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="">Select Year</option>
                {years.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Brand Color
              </label>
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setShowColorPicker(!showColorPicker)}
                  className="w-full flex items-center px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <div
                    className="w-6 h-6 rounded mr-2"
                    style={{ backgroundColor: editedCustomer.color || '#3B82F6' }}
                  />
                  <span className="text-gray-700">
                    {editedCustomer.color || 'Select color'}
                  </span>
                </button>
                {showColorPicker && (
                  <div className="absolute z-10 mt-2">
                    <div
                      className="fixed inset-0"
                      onClick={() => setShowColorPicker(false)}
                    />
                    <ChromePicker
                      color={editedCustomer.color || '#3B82F6'}
                      onChange={(color) => setEditedCustomer({
                        ...editedCustomer,
                        color: color.hex
                      })}
                    />
                  </div>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Contacts
              </label>
              <div className="space-y-2">
                {(editedCustomer.contacts || []).map((contact, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <span className="flex-1 px-3 py-2 bg-gray-50 rounded-md">{contact}</span>
                    <button
                      onClick={() => removeContact(index)}
                      className="text-red-600 hover:text-red-700"
                    >
                      Remove
                    </button>
                  </div>
                ))}
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newContact}
                    onChange={(e) => setNewContact(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && addContact()}
                    className="flex-1 border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Add new contact"
                  />
                  <button
                    onClick={addContact}
                    disabled={!newContact.trim()}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50"
                  >
                    Add
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-6 flex justify-end space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={loading || !editedCustomer.name.trim()}
              className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50"
            >
              {loading ? 'Saving...' : isNew ? 'Create Customer' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}